import { useState } from 'react';

const comparisons = [
  {
    title: 'Smith & Sons Plumbing',
    icon: 'ri-drop-line',
    before: {
      image: 'https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/008d407ee741fda3039f77c4d9d30861.png',
      issues: ['Full-width text walls', 'Clipart graphics', 'No mobile design', 'Buried contact info']
    },
    after: {
      image: 'https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/f605adc0fed1c4970e871f8b3b2df9a7.png',
      improvements: ['Bold "Call Now" CTA', 'Clean services grid', 'Mobile-first layout', 'Trust badges']
    },
    result: '3x more phone calls in first month'
  },
  {
    title: 'Bella Rose Salon & Spa',
    icon: 'ri-scissors-line',
    before: {
      image: 'https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/fa62778930a72d39600b6a3904b1126c.png',
      issues: ['Text-heavy pages', 'Confusing navigation', 'No online booking', 'Dated graphics']
    },
    after: {
      image: 'https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/49740ce2ad736f9f9383b71f2771896d.png',
      improvements: ['Prominent "Book Now"', 'Integrated reviews', 'Mobile booking', 'Modern design']
    },
    result: '5x increase in online bookings'
  },
  {
    title: "Lenhart's Landscaping",
    icon: 'ri-plant-line',
    before: {
      image: 'https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/45d9efa4f433e777f8882225de9b0404.png',
      issues: ['Stretched photos', 'Full-width text blocks', 'No estimate form', 'Table layout']
    },
    after: {
      image: 'https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/9d62f276d6b6dc8b397912fba12c9419.png',
      improvements: ['Bold "Free Estimate" CTA', 'Service area map', 'Portfolio gallery', 'Trust badges']
    },
    result: '4x more estimate requests'
  }
];

function ComparisonCard({ comparison }: { comparison: typeof comparisons[number] }) {
  const [showAfter, setShowAfter] = useState(false);

  return (
    <div className="bg-[#111111] rounded-2xl overflow-hidden border border-white/5 hover:border-white/10 transition-all duration-300">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B82F6]/10">
            <i className={`${comparison.icon} text-[#3B82F6] text-lg`}></i>
          </div>
          <h3 className="text-lg font-bold text-white">{comparison.title}</h3>
        </div>
      </div>

      {/* Toggle */}
      <div className="px-6 pb-4">
        <div className="bg-[#0A0A0A] rounded-xl p-1 flex relative">
          <button
            onClick={() => setShowAfter(false)}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-bold transition-all duration-300 cursor-pointer whitespace-nowrap relative z-10 ${
              !showAfter ? 'text-white' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <i className="ri-close-circle-line text-base"></i>
              Before
            </span>
          </button>
          <button
            onClick={() => setShowAfter(true)}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-bold transition-all duration-300 cursor-pointer whitespace-nowrap relative z-10 ${
              showAfter ? 'text-white' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <i className="ri-checkbox-circle-line text-base"></i>
              After
            </span>
          </button>
          {/* Sliding indicator */}
          <div
            className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-lg transition-all duration-300 ease-in-out ${
              showAfter
                ? 'left-[calc(50%+2px)] bg-[#3B82F6]/20 border border-[#3B82F6]/40'
                : 'left-[2px] bg-red-500/15 border border-red-500/30'
            }`}
          ></div>
        </div>
      </div>

      {/* Image */}
      <div className="px-6 relative">
        <div className="relative rounded-xl overflow-hidden w-full aspect-[3/2] bg-[#0A0A0A]">
          {/* Before image */}
          <img
            src={comparison.before.image}
            alt={`${comparison.title} before redesign`}
            className={`absolute inset-0 w-full h-full object-contain object-top transition-all duration-500 ${
              showAfter ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
            }`}
          />
          {/* After image */}
          <img
            src={comparison.after.image}
            alt={`${comparison.title} after redesign`}
            className={`absolute inset-0 w-full h-full object-contain object-top transition-all duration-500 ${
              showAfter ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          />
          {/* Corner badge */}
          <div className={`absolute top-3 left-3 px-3 py-1 rounded-lg text-xs font-bold backdrop-blur-sm transition-all duration-300 ${
            showAfter
              ? 'bg-[#3B82F6]/80 text-white'
              : 'bg-red-500/80 text-white'
          }`}>
            {showAfter ? 'REDESIGNED' : 'ORIGINAL'}
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="px-6 pt-5 pb-4">
        <div className={`transition-all duration-300 ${showAfter ? 'opacity-0 hidden' : 'opacity-100'}`}>
          <div className="flex flex-wrap gap-2">
            {comparison.before.issues.map((issue, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 bg-red-500/8 border border-red-500/15 text-red-400 text-xs font-semibold px-3 py-1.5 rounded-lg">
                <i className="ri-close-line text-sm"></i>
                {issue}
              </span>
            ))}
          </div>
        </div>
        <div className={`transition-all duration-300 ${showAfter ? 'opacity-100' : 'opacity-0 hidden'}`}>
          <div className="flex flex-wrap gap-2">
            {comparison.after.improvements.map((improvement, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 bg-[#3B82F6]/8 border border-[#3B82F6]/15 text-[#3B82F6] text-xs font-semibold px-3 py-1.5 rounded-lg">
                <i className="ri-check-line text-sm"></i>
                {improvement}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Result bar */}
      <div className="mx-6 mb-6 bg-gradient-to-r from-[#3B82F6]/10 to-transparent border border-[#3B82F6]/15 rounded-xl px-5 py-3 flex items-center gap-3">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#3B82F6]/15 shrink-0">
          <i className="ri-line-chart-line text-[#3B82F6] text-base"></i>
        </div>
        <p className="text-[#3B82F6] font-bold text-sm">
          {comparison.result}
        </p>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  return (
    <section id="before-after" className="py-24 px-6 bg-[#0A0A0A] relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#3B82F6]/3 to-transparent"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#3B82F6]/10 border border-[#3B82F6]/20 rounded-full px-4 py-2 mb-6">
            <i className="ri-contrast-2-line text-[#3B82F6] text-sm"></i>
            <span className="text-[#3B82F6] text-sm font-bold">Real Transformations</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            Before & After: <span className="text-[#3B82F6]">See the Difference</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Toggle between the old and new to see how we transform outdated websites into conversion machines
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {comparisons.map((comparison, index) => (
            <ComparisonCard key={index} comparison={comparison} />
          ))}
        </div>
      </div>
    </section>
  );
}